import { strainInputSchema, type Strain, type StrainInput, type StrainQuery } from '@shared';
import type { CatalogueMeta } from '../strainsApi';
import {
  getLocalCatalogueImport,
  isLocalCatalogueImportProvenance,
  type StrainWithLocalImport,
} from '../catalogueBootstrap/provenance';
import {
  createLocalProductImageRepository,
  type LocalProductImageRepository,
} from '../productImages/localProductImageRepository';
import { localRecordToStrainImage } from '../productImages/mapToStrainImage';
import { createClientId } from '../createClientId';
import { ProductRepositoryError } from './errors';
import {
  computeCatalogueMeta,
  filterAndSortStrains,
} from './filterSort';
import { createIdbProductStore } from './idbProductStore';
import {
  buildStrainFromInput,
  strainToDuplicateInput,
  toStrainSummary,
} from './strainRecord';
import type { ProductRepository, ProductStore } from './types';

export interface LocalProductRepositoryOptions {
  /** Injectable store (memory for tests; IndexedDB by default). */
  store?: ProductStore;
  /** Injectable local package-photo repository. */
  imageRepository?: LocalProductImageRepository;
  /** Override ID generator (defaults to createClientId). */
  createId?: () => string;
}

/**
 * Browser-local product repository. Persists Strain records through a
 * ProductStore (IndexedDB in production). Package photos live in a dedicated
 * image store and are hydrated onto Strain.images for UI display.
 */
export function createLocalProductRepository(
  options: LocalProductRepositoryOptions = {},
): ProductRepository {
  const store = options.store ?? createIdbProductStore();
  const imageRepository =
    options.imageRepository ?? createLocalProductImageRepository();
  const createId = options.createId ?? createClientId;

  async function hydrate(strain: Strain): Promise<Strain> {
    const images = await imageRepository.list(strain.id);
    const primaryImage =
      images.find((img) => img.isPrimary) ?? images[0] ?? null;
    return { ...strain, images, primaryImage };
  }

  async function hydrateSummary(strain: Strain) {
    const records = await imageRepository.listRecords(strain.id);
    const thumbnailRecord =
      records.find((record) => record.isPrimary) ?? records[0] ?? null;
    const thumbnail = thumbnailRecord
      ? localRecordToStrainImage(thumbnailRecord)
      : null;
    const summary = toStrainSummary({
      ...strain,
      images: thumbnail ? [thumbnail] : [],
      primaryImage: thumbnail,
    });
    return { ...summary, imageCount: records.length };
  }

  async function requireRawStrain(id: string): Promise<Strain> {
    const found = await store.get(id);
    if (!found) throw ProductRepositoryError.notFound();
    return found;
  }

  async function requireStrain(id: string): Promise<Strain> {
    return hydrate(await requireRawStrain(id));
  }

  async function create(input: StrainInput): Promise<Strain> {
    const parsed = strainInputSchema.parse({
      ...input,
      sourceType:
        input.sourceType && input.sourceType !== 'spreadsheet'
          ? input.sourceType
          : 'local_manual',
    });
    const id = createId();
    const existing = await store.get(id);
    if (existing) {
      // Extremely unlikely with UUID; refuse silent overwrite.
      throw ProductRepositoryError.conflict(
        'Generated product id already exists; refusing to overwrite.',
      );
    }
    const strain = buildStrainFromInput(id, parsed);
    await store.put(strain);
    return hydrate(strain);
  }

  return {
    mode: 'local',

    async list(query: Partial<StrainQuery> = {}) {
      const all = await store.getAll();
      const filtered = filterAndSortStrains(all, query);
      return Promise.all(filtered.map((row) => hydrateSummary(row)));
    },

    async meta(): Promise<CatalogueMeta> {
      const all = await store.getAll();
      return computeCatalogueMeta(all);
    },

    async get(id: string) {
      return requireStrain(id);
    },

    create,

    async update(id: string, input: StrainInput) {
      const existing = await requireRawStrain(id);
      const parsed = strainInputSchema.parse(input);
      const nextInput: StrainInput =
        existing.sourceType === 'spreadsheet' || existing.productId
          ? { ...parsed, sourceType: 'spreadsheet' }
          : {
              ...parsed,
              sourceType: parsed.sourceType ?? 'local_manual',
            };
      // Persist without embedding display blob URLs into the product row.
      const updated = buildStrainFromInput(id, nextInput, {
        ...existing,
        images: [],
        primaryImage: null,
      });
      await store.put(updated);
      return hydrate(updated);
    },

    async archive(id: string, archived: boolean) {
      const existing = await requireRawStrain(id);
      const updated: Strain & StrainWithLocalImport = {
        ...existing,
        images: [],
        primaryImage: null,
        isArchived: archived,
        updatedAt: new Date().toISOString(),
      };
      const provenance = getLocalCatalogueImport(
        existing as StrainWithLocalImport,
      );
      if (provenance) updated.localCatalogueImport = provenance;
      await store.put(updated);
      return hydrate(updated);
    },

    async setVerified(id: string, verified: boolean) {
      const existing = await requireRawStrain(id);
      const updated: Strain & StrainWithLocalImport = {
        ...existing,
        images: [],
        primaryImage: null,
        isVerified: verified,
        updatedAt: new Date().toISOString(),
      };
      const provenance = getLocalCatalogueImport(
        existing as StrainWithLocalImport,
      );
      if (provenance) updated.localCatalogueImport = provenance;
      await store.put(updated);
      return hydrate(updated);
    },

    async duplicate(id: string) {
      const existing = await requireRawStrain(id);
      return create(strainToDuplicateInput(existing));
    },

    async remove(id: string) {
      const existing = await store.get(id);
      if (!existing) throw ProductRepositoryError.notFound();

      if (store.deletesImagesWithProduct) {
        // IndexedDB: product + images deleted in one transaction by the store.
        const deleted = await store.delete(id);
        if (!deleted) throw ProductRepositoryError.notFound();
        return { deleted: true };
      }

      // Non-atomic fallback for injectable/memory stores: image cleanup and
      // product deletion are separate. If product deletion fails after image
      // cleanup succeeds, images for that product may already be gone.
      await imageRepository.deleteAllForProduct(id);
      const deleted = await store.delete(id);
      if (!deleted) throw ProductRepositoryError.notFound();
      return { deleted: true };
    },

    async fieldSources(id: string) {
      await requireRawStrain(id);
      return [];
    },

    async importFromCatalogueBootstrap(mapped: Strain) {
      const provenance = getLocalCatalogueImport(
        mapped as StrainWithLocalImport,
      );
      if (!isLocalCatalogueImportProvenance(provenance)) {
        throw ProductRepositoryError.storage(
          'Bootstrap import requires localCatalogueImport provenance.',
        );
      }
      const id = createId();
      const existing = await store.get(id);
      if (existing) {
        throw ProductRepositoryError.conflict(
          'Generated product id already exists; refusing to overwrite.',
        );
      }
      // Refuse if this source server id was already imported.
      const all = await store.getAll();
      for (const row of all) {
        const prior = getLocalCatalogueImport(row as StrainWithLocalImport);
        if (prior?.sourceServerStrainId === provenance.sourceServerStrainId) {
          throw ProductRepositoryError.conflict(
            'This server product was already imported into this device catalogue.',
          );
        }
      }
      const now = new Date().toISOString();
      const record: Strain & StrainWithLocalImport = {
        ...mapped,
        id,
        sourceType: 'imported',
        saveStatus: 'complete',
        lastSyncedAt: null,
        images: [],
        primaryImage: null,
        createdAt: mapped.createdAt || now,
        updatedAt: now,
        localCatalogueImport: provenance,
      };
      await store.put(record);
      return hydrate(record);
    },
  };
}
